/**
 * Auto-Pilot dashboard cards: "Next automatic run" + "Recent activity".
 *
 * Both cards operate on the status + history payload — the API doesn't expose
 * a "next tick" endpoint, so we render the schedule cadence in plain language
 * instead of a resolved timestamp.
 *
 * Resilience note: `status.last_auto_campaign_at` / `status.fire_count` are
 * denormalized markers that have historically lagged behind the actual
 * campaign rows (backend marker bug, since fixed). Both cards therefore fall
 * back to the history list when the markers claim "nothing happened" but
 * history rows exist.
 */
import { CalendarClock, Zap } from 'lucide-react';
import { Surface } from '@/components/ui/surface';
import { Spinner } from '@/components/spinner';
import { Button } from '@/components/ui/button';
import type { AutoCampaignStatus, AutoCampaignHistoryItem } from '@/features/auto-campaign/types';

export interface NextScheduledCardProps {
  status: AutoCampaignStatus | undefined;
  /** Used as fallback for "Last run" when the status marker is stale. */
  history: AutoCampaignHistoryItem[] | undefined;
  onRunNow: () => void;
}

/**
 * The API doesn't expose the resolved next-tick timestamp — the tick worker
 * computes it internally. We show the schedule cadence in plain language
 * instead so the user at least knows the rhythm.
 */
export function NextScheduledCard({
  status,
  history,
  onRunNow,
}: NextScheduledCardProps): JSX.Element {
  // Prefer the status marker, but fall back to the newest history row so a
  // stale marker never shows "No runs yet" above a table full of runs.
  const lastRunIso =
    status?.last_auto_campaign_at ?? history?.[0]?.created_at ?? null;
  const lastRun = lastRunIso ? new Date(lastRunIso) : null;

  return (
    <Surface tone="default" className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" />
            Next automatic run
          </div>
          <div className="mt-2 text-sm">
            <div className="font-medium text-foreground">
              Sun &amp; Wed 22:00
            </div>
            <div className="text-xs text-muted-foreground">
              Digest lands in Telegram Mon &amp; Thu 11:00 · Asia/Kolkata
            </div>
          </div>
          {lastRun ? (
            <div className="mt-2 text-xs text-muted-foreground">
              Last run: {lastRun.toLocaleString()}
            </div>
          ) : (
            <div className="mt-2 text-xs text-muted-foreground">
              No runs yet — your first digest arrives after the next scheduled
              run.
            </div>
          )}
        </div>
      </div>
      <div className="mt-4">
        <Button size="sm" onClick={onRunNow}>
          <Zap className="h-3.5 w-3.5" />
          Run now
        </Button>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Fires a full run immediately — results in Telegram in ~1-3 hours.
        </p>
      </div>
    </Surface>
  );
}

export interface RecentActivityCardProps {
  status: AutoCampaignStatus | undefined;
  history: AutoCampaignHistoryItem[] | undefined;
}

export function RecentActivityCard({
  status,
  history,
}: RecentActivityCardProps): JSX.Element {
  // Count runs + successes in the last 7 days.
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const runsThisWeek = (history ?? []).filter(
    (h) => new Date(h.created_at).getTime() > cutoff,
  ).length;
  const succeeded = (history ?? []).filter(
    (h) =>
      new Date(h.created_at).getTime() > cutoff && h.status === 'completed',
  ).length;

  // If the denormalized counter is stale (0) but runs clearly exist, show at
  // least the history count instead of a contradictory zero.
  const totalRuns =
    status && status.fire_count > 0
      ? status.fire_count
      : (history?.length ?? 0);

  return (
    <Surface tone="default" className="p-4">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Recent activity
      </div>
      {status === undefined || history === undefined ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="h-3.5 w-3.5" /> Loading…
        </div>
      ) : (
        <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <StatCell label="Runs in last 7 days" value={runsThisWeek} />
          <StatCell label="Succeeded (7d)" value={succeeded} />
          <StatCell label="Total runs" value={totalRuns} />
          <StatCell label="Backlog topics" value={status.backlog_count} />
        </dl>
      )}
    </Surface>
  );
}

function StatCell({
  label,
  value,
}: {
  label: string;
  value: number;
}): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="text-xl font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
